import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import {
  FileText, ArrowRight, AlertTriangle, CheckCircle2, Clock, DollarSign,
  BookOpen, Loader2, Compass, Star, Wand2, Car, CreditCard,
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const catIcons = { immigration: BookOpen, social_security: CreditCard, dmv: Car };
const catLabels = { immigration: "Immigration", social_security: "Government Services", dmv: "DMV" };

export default function FormGuidePage() {
  const { formId } = useParams();
  const [forms, setForms] = useState([]);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        if (formId) {
          const res = await axios.get(`${API}/guides/${formId}`);
          setForm(res.data);
        } else {
          const res = await axios.get(`${API}/guides`);
          setForms(res.data);
        }
      } catch { /* ignore */ }
      setLoading(false);
    };
    load();
  }, [formId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="guide-loading">
        <Loader2 className="h-8 w-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  // Guides index - grouped by category
  if (!formId) {
    const grouped = {};
    forms.forEach((f) => {
      const cat = f.category || "other";
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(f);
    });
    const catOrder = ["immigration", "social_security", "dmv"];

    return (
      <main className="min-h-screen bg-background" data-testid="guides-index">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-20">
          <div className="text-center mb-16">
            <Badge variant="secondary" className="bg-teal-100 text-teal-800 hover:bg-teal-100 mb-4 font-medium">Immigration Guides</Badge>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 font-heading mb-4">
              Complete U.S. Immigration Guides
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Step-by-step guides for every major immigration and government form.
              Free explanations, required documents, common mistakes, and AI assistance.
            </p>
          </div>

          {catOrder.map((cat) => {
            const catForms = grouped[cat];
            if (!catForms || catForms.length === 0) return null;
            const Icon = catIcons[cat] || FileText;
            return (
              <div key={cat} className="mb-12" data-testid={`guide-category-${cat}`}>
                <div className="flex items-center gap-3 mb-6">
                  <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center">
                    <Icon className="h-5 w-5 text-slate-600" />
                  </div>
                  <h2 className="text-2xl font-semibold text-slate-900 font-heading">{catLabels[cat] || cat}</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {catForms.map((f) => (
                    <Link to={`/guides/${f.form_id}`} key={f.form_id}>
                      <Card className="border-slate-100 hover:border-teal-200 hover:shadow-lg transition-all duration-300 h-full card-hover" data-testid={`guide-card-${f.form_id}`}>
                        <CardContent className="p-6">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="h-12 w-12 rounded-xl bg-teal-50 flex items-center justify-center">
                              <Icon className="h-6 w-6 text-teal-700" />
                            </div>
                            <div>
                              <Badge variant="outline" className="text-xs capitalize mb-1">{f.agency}</Badge>
                              <h3 className="text-lg font-bold text-slate-900">{f.code}</h3>
                            </div>
                          </div>
                          <p className="text-base font-semibold text-slate-800 mb-2">{f.name}</p>
                          <p className="text-sm text-slate-500 line-clamp-3 mb-4">{f.description}</p>
                          <div className="flex flex-wrap gap-3 text-xs text-slate-400">
                            <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {f.estimated_time}</span>
                            <span className="flex items-center gap-1"><DollarSign className="h-3.5 w-3.5" /> {f.filing_fee}</span>
                            <Badge className={`text-xs ${f.difficulty === "hard" ? "bg-red-100 text-red-700" : f.difficulty === "medium" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
                              {f.difficulty}
                            </Badge>
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </main>
    );
  }

  // Single guide page (SEO)
  if (!form) return null;
  const allMistakes = (form.fields || []).flatMap((f) => f.common_mistakes || []);
  const uniqueMistakes = [...new Set(allMistakes)];

  return (
    <main className="min-h-screen bg-background" data-testid="guide-detail">
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-16">
        {/* Hero */}
        <div className="mb-10">
          <Link to="/guides" className="text-sm text-teal-700 hover:underline mb-4 inline-block" data-testid="back-to-guides">
            All Guides
          </Link>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <Badge variant="outline" className="text-xs capitalize">{form.category?.replace("_", " ")}</Badge>
            <Badge variant="outline" className="text-xs">{form.agency}</Badge>
            <Badge className={`text-xs ${form.difficulty === "hard" ? "bg-red-100 text-red-700" : form.difficulty === "medium" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
              {form.difficulty} difficulty
            </Badge>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 font-heading mb-3">
            {form.code}: {form.name}
          </h1>
          <p className="text-lg text-slate-600 mb-6">{form.description}</p>

          <div className="grid grid-cols-3 gap-4 mb-8">
            <Card className="border-slate-100">
              <CardContent className="p-4 text-center">
                <Clock className="h-5 w-5 text-slate-500 mx-auto mb-1" />
                <p className="text-sm font-semibold text-slate-900">{form.estimated_time}</p>
                <p className="text-xs text-slate-500">to complete</p>
              </CardContent>
            </Card>
            <Card className="border-slate-100">
              <CardContent className="p-4 text-center">
                <DollarSign className="h-5 w-5 text-slate-500 mx-auto mb-1" />
                <p className="text-sm font-semibold text-slate-900">{form.filing_fee}</p>
                <p className="text-xs text-slate-500">filing fee</p>
              </CardContent>
            </Card>
            <Card className="border-slate-100">
              <CardContent className="p-4 text-center">
                <FileText className="h-5 w-5 text-slate-500 mx-auto mb-1" />
                <p className="text-sm font-semibold text-slate-900">{form.fields?.length || 0} fields</p>
                <p className="text-xs text-slate-500">to fill out</p>
              </CardContent>
            </Card>
          </div>

          {/* CTA */}
          <div className="bg-teal-50 border border-teal-200 rounded-xl p-6 mb-8" data-testid="guide-cta">
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-xl bg-teal-100 flex items-center justify-center flex-shrink-0">
                <Wand2 className="h-6 w-6 text-teal-700" />
              </div>
              <div className="flex-1">
                <h3 className="text-base font-semibold text-slate-900 mb-1">Need help filling out this form?</h3>
                <p className="text-sm text-slate-600 mb-3">
                  Our AI wizard guides you through every field step by step, explains each question, and catches mistakes before you submit.
                </p>
                <Link to="/auth?mode=register">
                  <Button className="bg-teal-700 hover:bg-teal-800 text-white font-medium" data-testid="guide-start-wizard">
                    <Wand2 className="mr-2 h-4 w-4" /> Start AI Wizard Free
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        <Separator className="mb-8" />

        {/* Fields explained */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-slate-900 font-heading mb-6">What Each Field Means</h2>
          <div className="space-y-4">
            {(form.fields || []).map((field, i) => (
              <Card key={field.field_id} className="border-slate-100" data-testid={`guide-field-${field.field_id}`}>
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-bold text-teal-700">{i + 1}.</span>
                    <h3 className="text-sm font-semibold text-slate-900">{field.label}</h3>
                    {field.required && <Badge variant="outline" className="text-xs text-red-500 border-red-200">Required</Badge>}
                  </div>
                  <p className="text-sm text-slate-600 mb-2">{field.description}</p>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-sm text-slate-700"><strong>Tip:</strong> {field.help_text}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Common mistakes */}
        {uniqueMistakes.length > 0 && (
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 font-heading mb-6">Common Mistakes to Avoid</h2>
            <Card className="border-amber-200 bg-amber-50/30">
              <CardContent className="p-6">
                <div className="space-y-3">
                  {uniqueMistakes.slice(0, 12).map((m, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-slate-700">{m}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </section>
        )}

        {/* Bottom CTA */}
        <section className="bg-teal-700 rounded-2xl p-8 text-center" data-testid="guide-bottom-cta">
          <Compass className="h-10 w-10 text-teal-200 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-white mb-3 font-heading">Ready to complete {form.code}?</h2>
          <p className="text-teal-100 mb-6">Let our AI assistant guide you through every step.</p>
          <Link to="/auth?mode=register">
            <Button size="lg" className="bg-white text-teal-700 hover:bg-teal-50 font-medium px-8" data-testid="guide-bottom-start">
              Start Free Guide <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </section>
      </div>
    </main>
  );
}
